import { useCallback, useEffect, useState } from "react";
import { useLatestRef } from "../../hooks/useLatestRef";
import { useAnalytics } from "../analytics/hooks";

type SessionChoiceOptions<T> = {
	/** The `sessionStorage` key. */
	key: string;
	/** The analytics name of the choice (`filter` on its storage-error event). */
	name: string;
	/** A stored string back to a value; null when it can't be used. */
	parse: (raw: string) => T | null;
	serialize: (value: T) => string;
	/** The value a new session starts on — and what an unreadable stored value falls back to. */
	initial: T;
};

type InitialRead<T> = { value: T; readError: unknown };

const errorMessageOf = (error: unknown): string => (error instanceof Error ? error.message : String(error));

const readStored = <T>({ key, parse, initial }: SessionChoiceOptions<T>): InitialRead<T> => {
	try {
		const raw = window.sessionStorage.getItem(key);
		// DEFAULT: nothing stored is a new session — it starts on `initial`, the choice the caller documents.
		if (raw === null) return { value: initial, readError: null };
		const parsed = parse(raw);
		return parsed === null ? { value: initial, readError: new Error(`unreadable stored value "${raw}"`) } : { value: parsed, readError: null };
	} catch (error) {
		return { value: initial, readError: error };
	}
};

/**
 * One schedule-changes choice kept in `sessionStorage` (the Changes tab's who and "What changed" selections): it
 * survives a refresh and a move between pages, and resets when the tab closes. Storage failures — and
 * a stored value that can't be read — are tracked as `schedule_changes_filter_storage_error`, and the choice keeps
 * working in memory from `initial`.
 *
 * `options` is read once, on mount: pass constants.
 */
export const useSessionChoice = <T>(options: SessionChoiceOptions<T>): [T, (next: T) => void] => {
	const { trackError } = useAnalytics();
	const trackErrorRef = useLatestRef(trackError);
	const [initialRead] = useState(() => readStored(options));
	const [value, setValue] = useState<T>(initialRead.value);
	const [{ key, name, serialize }] = useState(options);

	useEffect(() => {
		if (initialRead.readError !== null) {
			trackErrorRef.current("schedule_changes_filter_storage_error", { filter: name, operation: "read", error_message: errorMessageOf(initialRead.readError) });
		}
	}, [initialRead, name, trackErrorRef]);

	const setChoice = useCallback(
		(next: T) => {
			setValue(next);
			try {
				window.sessionStorage.setItem(key, serialize(next));
			} catch (error) {
				trackErrorRef.current("schedule_changes_filter_storage_error", { filter: name, operation: "write", error_message: errorMessageOf(error) });
			}
		},
		[key, name, serialize, trackErrorRef],
	);

	return [value, setChoice];
};
